import { Link } from "@tanstack/react-router";
import { ArrowRight, Eye, EyeOff, Loader2, Lock, Mail } from "lucide-react";
import type { ReactNode } from "react";
import { LocalizedAnchor } from "@/components/localized-anchor";
import {
	MAIN_CONTENT_ID,
	PublicFooter,
	PublicHeader,
} from "@/components/public-layout";
import type { AuthProvider } from "@/lib/last-auth-provider";

const AUTH_GRID_CELLS = Array.from({ length: 44 }, (_, index) => ({
	id: `auth-grid-cell-${index}`,
	hiddenOnMobile: index >= 8,
}));

export function AuthPageShell({ children }: { children: ReactNode }) {
	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground">
			<PublicHeader />
			<main
				id={MAIN_CONTENT_ID}
				className="mx-auto flex w-full max-w-6xl flex-1 px-4 sm:px-6"
			>
				<div className="relative flex flex-1 flex-col overflow-hidden bg-border px-px pb-px [--auth-row-size:4.75rem] sm:[--auth-row-size:5.25rem]">
					<div className="relative grid flex-1 grid-cols-4 grid-rows-[minmax(var(--auth-row-size),1fr)_repeat(7,minmax(var(--auth-row-size),auto))_minmax(var(--auth-row-size),1fr)] gap-px sm:grid-cols-8">
						{AUTH_GRID_CELLS.map((cell) => (
							<div
								key={cell.id}
								aria-hidden="true"
								className={`pointer-events-none rounded-md bg-white dark:bg-black ${
									cell.hiddenOnMobile ? "hidden sm:block" : ""
								}`}
							/>
						))}
						<div className="col-span-4 row-start-2 row-end-9 flex flex-col space-y-6 rounded-md bg-white px-6 py-10 shadow-[0_0_0_1px_var(--border)] dark:bg-black sm:col-start-2 sm:col-end-8 sm:px-8 lg:col-start-3 lg:col-end-7">
							{children}
						</div>
					</div>
				</div>
			</main>
			<PublicFooter showCta={false} />
		</div>
	);
}

export function AuthHeader({
	eyebrow,
	title,
	subtitle,
	headingId,
}: {
	eyebrow: string;
	title: string;
	subtitle: string;
	headingId: string;
}) {
	return (
		<header className="space-y-1">
			<p className="font-mono text-[11px] tracking-normal text-muted-foreground/70">
				{eyebrow}
			</p>
			<h1
				id={headingId}
				className="text-2xl font-semibold tracking-[-0.04em] text-foreground"
			>
				{title}
			</h1>
			<p className="text-sm text-muted-foreground">{subtitle}</p>
		</header>
	);
}

export function AuthError({
	id,
	children,
}: {
	id: string;
	children: ReactNode;
}) {
	return (
		<div
			id={id}
			role="alert"
			aria-live="assertive"
			className="rounded-md border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive"
		>
			{children}
		</div>
	);
}

export function GoogleButton({
	loading,
	lastProvider,
	onClick,
}: {
	loading: boolean;
	lastProvider: AuthProvider | null;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={loading}
			className="flex w-full cursor-pointer items-center gap-3 rounded-md border border-border bg-muted px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent/40 disabled:cursor-not-allowed disabled:opacity-50"
		>
			{loading ? (
				<Loader2 className="size-5 shrink-0 animate-spin" />
			) : (
				<GoogleIcon />
			)}
			<span className="flex-1 text-center">
				{loading ? "Signing in..." : "Continue with Google"}
			</span>
			{lastProvider === "google" && <LastUsedBadge />}
		</button>
	);
}

export function Divider({ label = "or email" }: { label?: string }) {
	return (
		<div className="relative">
			<div className="absolute inset-0 flex items-center">
				<div className="h-px w-full bg-border" />
			</div>
			<div className="relative flex justify-center">
				<span className="bg-white px-4 text-xs uppercase tracking-wider text-muted-foreground dark:bg-black">
					{label}
				</span>
			</div>
		</div>
	);
}

export function TextInput(props: {
	id: string;
	label: string;
	type?: string;
	autoComplete: string;
	value: string;
	onChange: (value: string) => void;
	placeholder: string;
	errorId?: string;
}) {
	return (
		<div>
			<label
				htmlFor={props.id}
				className="mb-2 block text-sm font-medium text-foreground"
			>
				{props.label}
			</label>
			<div className="relative">
				<Mail className="absolute left-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
				<input
					id={props.id}
					type={props.type ?? "text"}
					autoComplete={props.autoComplete}
					value={props.value}
					onChange={(event) => props.onChange(event.target.value)}
					required
					aria-invalid={!!props.errorId}
					aria-describedby={props.errorId}
					className={INPUT_CLASS}
					placeholder={props.placeholder}
				/>
			</div>
		</div>
	);
}

export function PasswordInput(props: {
	id: string;
	label?: string;
	autoComplete: "current-password" | "new-password";
	value: string;
	onChange: (value: string) => void;
	showPassword: boolean;
	onTogglePassword: () => void;
	placeholder: string;
	errorId?: string;
	describedBy?: string;
	trailingLabel?: ReactNode;
}) {
	return (
		<div>
			<div className="mb-2 flex items-center justify-between">
				<label
					htmlFor={props.id}
					className="text-sm font-medium text-foreground"
				>
					{props.label ?? "Password"}
				</label>
				{props.trailingLabel}
			</div>
			<div className="relative">
				<Lock className="absolute left-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
				<input
					id={props.id}
					type={props.showPassword ? "text" : "password"}
					autoComplete={props.autoComplete}
					value={props.value}
					onChange={(event) => props.onChange(event.target.value)}
					required
					aria-invalid={!!props.errorId}
					aria-describedby={props.describedBy ?? props.errorId}
					className={`${INPUT_CLASS} pr-11`}
					placeholder={props.placeholder}
				/>
				<button
					type="button"
					onClick={props.onTogglePassword}
					aria-label={props.showPassword ? "Hide password" : "Show password"}
					className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent/50"
				>
					{props.showPassword ? (
						<EyeOff className="size-4" />
					) : (
						<Eye className="size-4" />
					)}
				</button>
			</div>
		</div>
	);
}

const INPUT_CLASS =
	"w-full rounded-md border border-border bg-muted py-2.5 pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground transition-colors focus:border-brand-accent focus:outline-none focus:ring-2 focus:ring-brand-accent/30";

export function SubmitButton({
	loading,
	loadingLabel,
	children,
}: {
	loading: boolean;
	loadingLabel: string;
	children: ReactNode;
}) {
	return (
		<button
			type="submit"
			disabled={loading}
			aria-busy={loading}
			className="flex w-full items-center justify-center gap-2 rounded-md bg-brand-accent px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
		>
			{loading ? (
				<>
					<Loader2 className="size-4 animate-spin" />
					{loadingLabel}
				</>
			) : (
				<>
					{children}
					<ArrowRight className="size-4" />
				</>
			)}
		</button>
	);
}

export function AuthSwitchLink({
	question,
	linkText,
	to,
	redirect,
}: {
	question: string;
	linkText: string;
	to: "/login" | "/signup";
	redirect: string | null;
}) {
	return (
		<p className="text-center text-sm text-muted-foreground">
			{question}{" "}
			<Link
				to={to}
				search={redirect ? { redirect } : {}}
				className="font-medium text-brand-accent transition-colors hover:text-foreground"
			>
				{linkText}
			</Link>
		</p>
	);
}

export function TermsNotice() {
	return (
		<p className="text-center text-xs text-muted-foreground">
			By creating an account, you agree to our{" "}
			<LocalizedAnchor
				href="/terms"
				target="_blank"
				rel="noopener noreferrer"
				className="underline transition-colors hover:text-foreground"
			>
				Terms of Service
			</LocalizedAnchor>{" "}
			and{" "}
			<LocalizedAnchor
				href="/privacy"
				target="_blank"
				rel="noopener noreferrer"
				className="underline transition-colors hover:text-foreground"
			>
				Privacy Policy
			</LocalizedAnchor>
			.
		</p>
	);
}

export function PasswordStrength({
	id,
	strength,
}: {
	id: string;
	strength: { score: number; label: string; kmColor: string };
}) {
	return (
		<div id={id} className="-mt-2">
			<meter
				className="sr-only"
				value={strength.score}
				min={1}
				max={5}
				aria-label={`Password strength: ${strength.label}`}
			/>
			<div className="mb-1 flex gap-1" aria-hidden="true">
				{[1, 2, 3, 4, 5].map((level) => (
					<div
						key={level}
						className="h-1 flex-1 rounded-full bg-muted transition-colors"
						style={
							level <= strength.score
								? { backgroundColor: strength.kmColor }
								: undefined
						}
					/>
				))}
			</div>
			<p
				className="text-right text-xs text-muted-foreground"
				aria-live="polite"
			>
				{strength.label}
			</p>
		</div>
	);
}

export function LastUsedBadge() {
	return (
		<span className="ml-auto flex-shrink-0 rounded-full border border-brand-accent/40 bg-brand-accent/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-brand-accent">
			Last used
		</span>
	);
}

function GoogleIcon() {
	return (
		<svg
			className="size-5 flex-shrink-0"
			viewBox="0 0 24 24"
			aria-hidden="true"
			focusable="false"
		>
			<path
				fill="#4285F4"
				d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
			/>
			<path
				fill="#34A853"
				d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
			/>
			<path
				fill="#FBBC05"
				d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
			/>
			<path
				fill="#EA4335"
				d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
			/>
		</svg>
	);
}
